import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "../../../src/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Search, Eye, Trash2, RefreshCw } from "lucide-react";
import { format } from "date-fns";

interface PrayerRequest {
  id: string;
  title: string;
  description: string | null;
  requester_name: string | null;
  requester_email: string | null;
  status: string;
  is_anonymous: boolean;
  region_id: string | null;
  created_at: string;
  regions?: {
    name: string;
  };
}

interface PrayerRequestsManagerProps {
  userRole: string;
  userRegion?: string;
  isGlobalAdmin: boolean;
}

export const PrayerRequestsManager: React.FC<PrayerRequestsManagerProps> = ({
  userRole,
  userRegion,
  isGlobalAdmin
}) => {
  const [prayerRequests, setPrayerRequests] = useState<PrayerRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [regionFilter, setRegionFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedRequest, setSelectedRequest] = useState<PrayerRequest | null>(null);
  const [regions, setRegions] = useState<{ id: string; name: string }[]>([]);
  const [showViewDialog, setShowViewDialog] = useState(false);
  
  const isRegionalAdmin = ['regional_admin', 'Regional Admin'].includes(userRole);

  useEffect(() => {
    fetchRegions();
    fetchPrayerRequests();
  }, [statusFilter, regionFilter]);

  const fetchRegions = async () => {
    try {
      const { data, error } = await supabase
        .from('regions')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      setRegions(data || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchPrayerRequests = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('prayer_requests')
        .select('*, regions(name)')
        .order('created_at', { ascending: false });

      // Regional admin only sees their region
      if (isRegionalAdmin && userRegion) {
        query = query.eq('region_id', userRegion);
      } else if (!isGlobalAdmin && userRegion) {
        query = query.eq('region_id', userRegion);
      }

      // Apply filters
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      if (regionFilter !== 'all' && isGlobalAdmin) {
        query = query.eq('region_id', regionFilter);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      setPrayerRequests(data || []);
    } catch (error) {
      console.error('Error fetching prayer requests:', error);
      toast({
        title: "Error",
        description: "Failed to load prayer requests",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('prayer_requests')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Prayer request status updated"
      });

      fetchPrayerRequests();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const deleteRequest = async (id: string) => {
    if (!confirm('Are you sure you want to delete this prayer request?')) return;

    try {
      const { error } = await supabase
        .from('prayer_requests')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Prayer request deleted"
      });

      fetchPrayerRequests();
      setShowViewDialog(false);
    } catch (error) {
      console.error('Error deleting request:', error);
      toast({
        title: "Error",
        description: "Failed to delete request",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: "default" | "secondary" | "outline" | "destructive" } = {
      active: "default",
      answered: "secondary",
      archived: "outline"
    };
    return <Badge variant={variants[status] || "default"}>{status}</Badge>;
  };

  const filteredRequests = prayerRequests.filter(req => {
    const searchLower = searchQuery.toLowerCase();
    return (
      req.title.toLowerCase().includes(searchLower) ||
      (req.description?.toLowerCase().includes(searchLower)) ||
      (req.requester_name?.toLowerCase().includes(searchLower))
    );
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Prayer Requests Management</CardTitle>
          <CardDescription>View and manage prayer requests from members and visitors</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search requests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="answered">Answered</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>

            {isGlobalAdmin && (
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {regions.map(region => (
                    <SelectItem key={region.id} value={region.id}>
                      {region.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button onClick={fetchPrayerRequests} variant="outline" size="icon">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Prayer Requests List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="text-sm text-muted-foreground mt-2">Loading...</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No prayer requests found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map((request) => (
                <Card key={request.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{request.title}</h3>
                          {getStatusBadge(request.status)}
                          {request.is_anonymous && (
                            <Badge variant="outline">Anonymous</Badge>
                          )}
                        </div>
                        
                        {request.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {request.description}
                          </p>
                        )}
                        
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          {!request.is_anonymous && request.requester_name && (
                            <span>Requester: {request.requester_name}</span>
                          )}
                          {request.regions && (
                            <span>Region: {request.regions.name}</span>
                          )}
                          <span>Date: {format(new Date(request.created_at), 'MMM dd, yyyy')}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowViewDialog(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        <Select
                          value={request.status}
                          onValueChange={(value) => updateStatus(request.id, value)}
                        >
                          <SelectTrigger className="w-[130px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="answered">Answered</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedRequest?.title}</DialogTitle>
            <DialogDescription>
              {selectedRequest?.is_anonymous ? 'Anonymous Request' : `By ${selectedRequest?.requester_name}`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Description</h4>
                <p className="text-sm text-muted-foreground">
                  {selectedRequest.description || 'No description provided'}
                </p>
              </div>
              
              {!selectedRequest.is_anonymous && (
                <>
                  {selectedRequest.requester_email && (
                    <div>
                      <h4 className="text-sm font-medium mb-1">Email</h4>
                      <p className="text-sm text-muted-foreground">{selectedRequest.requester_email}</p>
                    </div>
                  )}
                </>
              )}
              
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium">Status:</h4>
                {getStatusBadge(selectedRequest.status)}
              </div>
              
              {selectedRequest.regions && (
                <div>
                  <h4 className="text-sm font-medium mb-1">Region</h4>
                  <p className="text-sm text-muted-foreground">{selectedRequest.regions.name}</p>
                </div>
              )}
              
              <div>
                <h4 className="text-sm font-medium mb-1">Submitted</h4>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(selectedRequest.created_at), 'PPpp')}
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="destructive"
                  onClick={() => deleteRequest(selectedRequest.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
